import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'

import Column from './column'

const fetchAircraftsFromApi = url =>
  fetch(url)
    .then(res => res.json())
    .then(aircrafts => {
      const result = {}
      aircrafts.forEach(aircraft => {
        result[aircraft.nid] = {
          name: aircraft.title,
          sizeClass: aircraft.field_size,
          selected: false,
        }
      })
      return result
    })

const fetchClassesFromApi = url =>
  fetch(url)
    .then(res => res.json())
    .then(sizes => {
      const result = {}
      sizes.forEach((size, index) => {
        result[size.tid] = {
          name: size.name,
          description: size.field_aircraft_size_short_d,
          selected: false,
          order: index,
        }
      })
      return result
    })

const hashToList = hash => Object.keys(hash).map(id => ({ id, ...hash[id] }))

const getSelectionList = hash => hashToList(hash).filter(el => el.selected)

const setSelection = (hash, id, isSelected) => ({
  ...hash,
  [id]: {
    ...hash[id],
    selected: isSelected,
  },
})

function debounce(fn, ms) {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn(...args), ms)
  }
}

const debouncedScroll = debounce(() => {
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}, 100)

const AircraftPicker = props => {
  const { t } = useTranslation()

  const [isOpened, setIsOpened] = useState(false)
  const [classesHash, setClassesHash] = useState({})
  const [aircraftsHash, setAircraftsHash] = useState({})

  const aircraftSelection = getSelectionList(aircraftsHash)
  const classesSelection = getSelectionList(classesHash)
  const totalSelection = [...classesSelection, ...aircraftSelection]

  const aircraftsList = hashToList(aircraftsHash)

  const close = () => {
    setIsOpened(false)
  }

  const isPlaceholderDisplayed = totalSelection.length === 0
  const placeholderText = isPlaceholderDisplayed
    ? t('Type of aircraft')
    : totalSelection.map(el => el.name).join(', ')

  const serverValue = totalSelection.map(el => el.nameEn).join(', ')

  const handleItemClick = id => {
    setAircraftsHash(
      setSelection(aircraftsHash, id, !aircraftsHash[id].selected)
    )
  }

  const handleMainItemClick = id => () => {
    setClassesHash(setSelection(classesHash, id, !classesHash[id].selected))
  }

  const clearables = [
    [classesHash, setClassesHash],
    [aircraftsHash, setAircraftsHash],
  ]
  const clearAll = () =>
    clearables.forEach(([value, updater]) =>
      getSelectionList(value).forEach(({ id }) =>
        updater(setSelection(value, id, false))
      )
    )

  // close on click outside
  const wrapper = useRef(null)
  const validateableBlock = useRef(null)
  useEffect(() => {
    const checkClick = e => {
      if (
        isOpened &&
        !wrapper.current.contains(e.target) &&
        !validateableBlock.current.contains(e.target)
      ) {
        close()
      }
    }
    document.body.addEventListener('click', checkClick)
    return () => {
      document.body.removeEventListener('click', checkClick)
    }
  }, [isOpened])

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let hashes = []

    Promise.all([
      fetchAircraftsFromApi(window.globaljetRestApi.aircraft),
      fetchClassesFromApi(window.globaljetRestApi.size),
      fetchAircraftsFromApi(window.globaljetRestApi.aircraftEn),
      fetchClassesFromApi(window.globaljetRestApi.sizeEn),
    ]).then(result => {
      const [aircrafts, sizes, aircraftsEn, sizesEn] = result
      hashes = [aircrafts, sizes]
      setIsLoading(false)
      Object.keys(aircrafts).forEach(id => {
        aircrafts[id].nameEn = aircraftsEn[id].name
      })
      Object.keys(sizes).forEach(id => {
        sizes[id].nameEn = sizesEn[id].name
      })
      setAircraftsHash(aircrafts)
      setClassesHash(hashes[1])
    })

    const checkClick = e => {
      const pickerTrigger = e.target.closest('[data-aircraft-picker-trigger]')
      if (pickerTrigger) {
        e.preventDefault()
        debouncedScroll()

        const aircraftId = pickerTrigger.dataset.aircraftPickerTrigger
        // select aircraft type
        // setAircraftsHash(setSelection(hashes[0], aircraftId, true))
        const aircraft = hashes[0][aircraftId]
        if (aircraft) {
          // select size class
          setClassesHash(setSelection(hashes[1], aircraft.sizeClass, true))
        }
      }
    }
    document.body.addEventListener('click', checkClick)
    document.body.addEventListener('touchend', checkClick)
    return () => {
      document.body.removeEventListener('click', checkClick)
      document.body.removeEventListener('touchend', checkClick)
    }
  }, [])

  const columns = isLoading
    ? t('Loading')
    : Object.entries(classesHash)
        .sort((a, b) => Math.sign(a[1].order - b[1].order))
        .map(([id, column]) => (
          <Column
            key={id}
            id={id}
            title={column.name}
            info={column.description}
            listItems={aircraftsList
              .filter(aircraft => aircraft.sizeClass === id)
              .sort((a, b) => {
                const lca = a.name.toLowerCase()
                const lcb = b.name.toLowerCase()
                if (lca > lcb) return 1
                if (lca < lcb) return -1
                return 0
              })}
            onItemClick={handleItemClick}
            onMainItemClick={handleMainItemClick(id)}
            disabled={classesSelection.length === props.maxSelection}
            selected={classesHash[id].selected}
          />
        ))

  return (
    <>
      <label
        className='ticket__label ticket__label--aircraft2 ticket__label--icon-top ticket__label--icon-top-left input-block'
        data-validateable-block
        ref={validateableBlock}
      >
        <input
          className={classnames([
            'ticket__input ticket__input--aircraft input-field',
            { 'ticket__input--placeholder': isPlaceholderDisplayed },
          ])}
          readOnly
          onFocus={setIsOpened.bind(null, true)}
          value={placeholderText}
        />

        <div className='input-error'>{t('This field is required')}</div>

        <input
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
          // required
          data-validateable-input
          name={props.inputName}
          value={serverValue}
        />
      </label>

      {isOpened && (
        <div
          className={classnames(
            'drop-down ticket__drop-down',
            props.wrapperClass
          )}
          ref={wrapper}
        >
          {/* <div className='drop-down__info-text'>
            <p>* {t('Select up to 1 choice')}</p>
          </div> */}

          <div className='flex-container drop-down__list-container'>
            {columns}
          </div>

          <div className='drop-down__info-container'>
            <div className='flex-container align-justify drop-down__buttons'>
              <button
                className='drop-down__button drop-down__button--clear'
                type='button'
                onClick={clearAll}
              >
                {t('Clear all')}
              </button>
              <button
                className='drop-down__button drop-down__button--save'
                type='button'
                onClick={close}
                onTouchEnd={close}
              >
                {t('Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

AircraftPicker.propTypes = {
  inputName: PropTypes.string,
  maxSelection: PropTypes.number.isRequired,
  wrapperClass: PropTypes.string,
}

export default AircraftPicker
